import ListLoader from './ListLoader.js';

/**
 * MindMap
 * 課題解決マインドマップ（サービストップ §D）。
 * タブ＝カテゴリ（PCは横タブ=Switcher / SPはアコーディオン型タブ=Accordion。CSS・既存JSで両対応）。
 * 各カテゴリパネルの中身（タグチップ＋悩み項目リスト）を JSON から動的生成する。
 *  - データは複数JSON（mindmap_{key}.json）を ListLoader で取得（ブログ/お知らせと同方式）。
 *  - チップ＝そのカテゴリの tags（出現順）。クリックで単一選択し、該当タグの項目だけ表示。
 *  - 項目＝悩み(title)のアコーディオン。開くと 課題(problem)→解決策(solution) と 関連サービス を表示。
 * カテゴリ切替（タブ/アコーディオン）は Switcher / Accordion が担当（このクラスはパネル内のみ）。
 *
 * @example
 * <div class="p-mindmap js-mindmap" data-mindmap-base="/service/">
 *   <div class="p-mindmap-panel …" data-mindmap-cat="ad"> … [data-mindmap-chips] [data-mindmap-list] … </div>
 * </div>
 */
export default class MindMap {
  constructor(options = {}) {
    this.root = document.querySelector(options.selector || '.js-mindmap');
    if (!this.root) return;
    this.base = this.root.dataset.mindmapBase || '/service/';
    this.panels = Array.from(this.root.querySelectorAll('[data-mindmap-cat]'));
    this.init();
  }

  init() {
    this.panels.forEach((panel) => this.setupPanel(panel));
  }

  async setupPanel(panel) {
    const key = panel.dataset.mindmapCat;
    const chipsEl = panel.querySelector('[data-mindmap-chips]');
    const listEl = panel.querySelector('[data-mindmap-list]');
    if (!key || !chipsEl || !listEl) return;

    const items = await ListLoader.load({ basePath: this.base, type: 'mindmap', years: [key] });
    if (!items.length) return;

    // タグ（出現順・重複排除）＝チップ
    const tags = [];
    items.forEach((it) => (it.tags || []).forEach((t) => {
      if (!tags.includes(t)) tags.push(t);
    }));

    const state = { tag: tags[0] || null };

    chipsEl.innerHTML = tags
      .map((t, i) => `<button type="button" class="p-mindmap-chip${i === 0 ? ' is-active' : ''}" data-tag="${t}">${t}</button>`)
      .join('');

    const renderList = () => {
      const filtered = state.tag ? items.filter((it) => (it.tags || []).includes(state.tag)) : items;
      listEl.innerHTML = filtered.map((it) => this.itemHTML(it)).join('');
    };
    renderList();

    // チップ＝単一選択
    chipsEl.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-tag]');
      if (!btn) return;
      state.tag = btn.dataset.tag;
      chipsEl.querySelectorAll('.p-mindmap-chip').forEach((c) => c.classList.toggle('is-active', c === btn));
      renderList();
    });

    // 悩み項目の開閉。js-ac 規約に合わせ is-active をトグル（チップ再描画で項目が作り直されるため、
    // 読み込み時バインドのグローバル Accordion では追えない。委譲＝再描画に強い。開閉アニメはCSSのgrid）。
    listEl.addEventListener('click', (e) => {
      const bar = e.target.closest('.js-ac-trigger');
      if (!bar) return;
      const item = bar.closest('.p-mindmap-item');
      const open = item.classList.toggle('is-active');
      bar.setAttribute('aria-expanded', String(open));
    });
  }

  itemHTML(it) {
    const rel = (it.relatedServices || [])
      .map((r) => `<a href="${r.url || '#'}">${r.label}</a>`)
      .join('');
    return `
      <div class="p-mindmap-item js-ac">
        <button type="button" class="p-mindmap-item-bar js-ac-trigger" aria-expanded="false">
          <span class="title">${it.title || ''}</span>
          <span class="ico" aria-hidden="true"></span>
        </button>
        <div class="p-mindmap-item-body js-ac-content">
          <div class="p-mindmap-item-inner">
            <div class="flow">
              <div class="box cl-problem">
                <span class="label">課題（根本原因）：</span>
                <p>${it.problem || ''}</p>
              </div>
              <div class="box cl-solution">
                <span class="label">解決策（提供価値）：</span>
                <p>${it.solution || ''}</p>
              </div>
            </div>
            ${rel ? `<p class="related"><span class="label">関連サービス：</span>${rel}</p>` : ''}
          </div>
        </div>
      </div>`;
  }
}
